import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { CT } from '@parsina/framework/tokens';
import { Button } from '@parsina/framework';
import { Play, Clock } from 'lucide-react';
import { buildHeroModel, formatDuration } from '../../lib/appointments';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';

// The next-patient / open-visit hero card (mobile concept §4): a gradient card
// naming who the doctor calls in next. It escalates with an open visit — brand
// gradient within the slot, amber ring past it, red past 2× — from the same
// buildHeroModel the web hero uses; only the rendering is mobile's own.

const HERO_CHIP = {
  normal:   { bg: 'rgba(255,255,255,0.15)', color: 'white' },
  over:     { bg: C.amberLight, color: CT.warnInk },
  critical: { bg: C.redLight,  color: CT.dangerInk },
};

const MobileNextPatientCard = ({ apt, nowMin, nowMs, onStart, onHistory, onEnd, testId = 'reception-hero' }) => {
  const { t } = useTranslation();
  const m = buildHeroModel(apt, nowMin, nowMs, t);
  const chip = HERO_CHIP[m.level];

  return (
    <div
      data-testid={testId}
      style={{
        margin: '0 1rem 0.75rem', background: m.background,
        borderRadius: '1rem', padding: '1.125rem',
        boxShadow: m.level === 'critical' ? '0 6px 20px rgba(153,27,27,0.3)' : `0 6px 20px ${soft(C.navy, 0.28)}`,
        border: m.level === 'over' ? `2px solid ${C.amber}` : 'none',
        position: 'relative', overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: '-1.25rem', insetInlineStart: '-1.25rem', width: '5.625rem', height: '5.625rem', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

      <div style={{ fontSize: '0.625rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.05em', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.375rem', flexWrap: 'wrap' }}>
        <span style={{ width: '0.4375rem', height: '0.4375rem', borderRadius: '50%', background: m.dotColor, boxShadow: `0 0 0 3px ${m.dotColor}4D`, display: 'inline-block' }} />
        {m.heroLabel}
        {m.inVisit && m.elapsedMin != null && (
          <span style={{ fontSize: '0.625rem', fontWeight: 800, borderRadius: '1.25rem', padding: '0.125rem 0.5rem', background: chip.bg, color: chip.color, display: 'inline-flex', alignItems: 'center', gap: '0.1875rem' }}>
            <Clock size={11} strokeWidth={2.4} aria-hidden />{' '}
            {t('dashboard.hero.sinceStart', { duration: formatDuration(t, m.elapsedMin) })}
            {m.level !== 'normal' && t('dashboard.hero.overSlot')}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
          <div style={{
            width: '2.75rem', height: '2.75rem', borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
            border: '2px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.9375rem', fontWeight: 800, color: 'white', flexShrink: 0,
          }}>{m.initials}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.9375rem', fontWeight: 800, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {apt.name || t('dashboard.hero.unknownPatient')}
            </div>
            {apt.specialty && (
              <div style={{ fontSize: '0.71875rem', color: 'rgba(255,255,255,0.65)', marginTop: '0.125rem' }}>{apt.specialty}</div>
            )}
          </div>
        </div>
        <div style={{ textAlign: 'center', flexShrink: 0 }}>
          <div style={{ background: 'rgba(255,255,255,0.15)', borderRadius: '0.625rem', padding: '0.3125rem 0.75rem', fontSize: '1.0625rem', fontWeight: 800, color: 'white' }}>
            {apt.time}
          </div>
          <div style={{ fontSize: '0.65625rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.25rem' }}>{m.waitLabel}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.875rem' }}>
        <Button
          variant="ghost" size="sm"
          data-testid={`${testId}-history`}
          className="flex-1 min-h-11 text-[0.8125rem] font-bold text-white hover:bg-white/25"
          style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.22)' }}
          onClick={onHistory}
        >{t('dashboard.hero.historyShort')}</Button>
        {m.inVisit && (
          <Button
            variant="ghost" size="sm"
            data-testid={`${testId}-end`}
            className="flex-1 min-h-11 text-[0.8125rem] font-extrabold"
            style={m.level === 'normal'
              ? { background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.22)' }
              : { background: 'white', color: CT.dangerInk }}
            onClick={onEnd}
          >{t('dashboard.hero.endShort')}</Button>
        )}
        <Button
          variant="ghost" size="sm"
          data-testid={`${testId}-start`}
          className="flex-[2] min-h-11 gap-1.5 text-[0.8125rem] font-extrabold"
          style={{ background: 'white', color: m.level === 'critical' ? CT.dangerInk : C.navy }}
          onClick={onStart}
        >
          <Play size={14} strokeWidth={2.6} fill="currentColor" aria-hidden /> {m.inVisit ? t('dashboard.hero.continueVisit') : t('dashboard.hero.startVisit')}
        </Button>
      </div>
    </div>
  );
};

export default MobileNextPatientCard;
